import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import {
  Delete as DeleteIcon,
  DragHandle as DragIcon
} from '@mui/icons-material'

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text', description: 'Single line text input' },
  { value: 'textarea', label: 'Long Text', description: 'Multi-line text area' },
  { value: 'radio', label: 'Single Choice', description: 'Radio buttons - select one option' },
  { value: 'checkbox', label: 'Multiple Choice', description: 'Checkboxes - select multiple options' },
  { value: 'select', label: 'Dropdown', description: 'Dropdown menu - select one option' }
]

function QAFormTemplateDialog ({
  open,
  onClose,
  editingQuestion = null,
  classes,
  formData,
  setFormData,
  newOption,
  setNewOption,
  handleAddOption,
  handleRemoveOption,
  saving,
  onSave
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ style: { borderRadius: 16, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-text-primary)' } }}>
      <DialogTitle style={{ borderBottom: '1px solid var(--color-border-light)' }}>
        <Typography variant="h6" style={{ fontWeight: 600, color: 'var(--color-deep-blue)' }}>
          {editingQuestion ? 'Edit Question' : 'Add Question'}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent} style={{ padding: 24 }}>
        {/* Question Text */}
        <TextField
          label="Question Text"
          fullWidth
          margin="normal"
          variant="outlined"
          value={formData.questionText}
          onChange={(e) => setFormData({ ...formData, questionText: e.target.value })}
          placeholder="e.g., Did the agent follow the greeting script?"
          InputProps={{ style: { borderRadius: 12 } }}
        />

        {/* Question Type */}
        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel>Question Type</InputLabel>
          <Select
            value={formData.questionType}
            onChange={(e) => setFormData({ ...formData, questionType: e.target.value, options: [] })}
            label="Question Type"
            style={{ borderRadius: 12 }}
          >
            {QUESTION_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Box>
                  <Typography variant="body2">{type.label}</Typography>
                  <Typography variant="caption" color="textSecondary">{type.description}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Options for select-type questions */}
        {['radio', 'checkbox', 'select'].includes(formData.questionType) && (
          <Box className={classes.optionsList}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
              Options
            </Typography>

            {formData.options.map((option, index) => (
              <Box key={index} className={classes.optionItem}>
                <DragIcon style={{ color: 'var(--color-text-light)' }} />
                <Box flex={1}>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="caption" color="textSecondary">Value: {option.value}</Typography>
                </Box>
                <IconButton size="small" onClick={() => handleRemoveOption(index)}>
                  <DeleteIcon fontSize="small" style={{ color: '#EF4444' }} />
                </IconButton>
              </Box>
            ))}

            <Box display="flex" mt={2} style={{ gap: 8 }}>
              <TextField
                size="small"
                label="Value"
                value={newOption.value}
                onChange={(e) => setNewOption({ ...newOption, value: e.target.value })}
                style={{ flex: 1 }}
                InputProps={{ style: { borderRadius: 8 } }}
              />
              <TextField
                size="small"
                label="Label"
                value={newOption.label}
                onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                style={{ flex: 1 }}
                InputProps={{ style: { borderRadius: 8 } }}
              />
              <Button
                variant="outlined"
                onClick={handleAddOption}
                style={{ borderRadius: 8 }}
              >
                Add
              </Button>
            </Box>
          </Box>
        )}

        {/* Display Order */}
        <TextField
          label="Display Order"
          type="number"
          fullWidth
          margin="normal"
          variant="outlined"
          value={formData.displayOrder}
          onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value, 10) || 0 })}
          InputProps={{ style: { borderRadius: 12 }, inputProps: { min: 0 } }}
          helperText="Lower numbers appear first"
        />

        {/* Required Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={formData.isRequired}
              onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
              color="primary"
            />
          }
          label="Required question"
          style={{ marginTop: 8 }}
        />

        {/* Active Toggle */}
        {editingQuestion && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{
              backgroundColor: formData.isActive ? '#F0FDF4' : '#FEF2F2',
              padding: '12px 16px',
              borderRadius: 12,
              marginTop: 16,
              border: formData.isActive ? '1px solid #BBF7D0' : '1px solid #FECACA'
            }}
          >
            <Box>
              <Typography variant="subtitle2" style={{ fontWeight: 600, color: formData.isActive ? '#166534' : '#991B1B' }}>
                {formData.isActive ? 'Question is Active' : 'Question is Inactive'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {formData.isActive
                  ? 'This question will appear in the manual QA form'
                  : 'This question will NOT appear in the manual QA form'}
              </Typography>
            </Box>
            <Switch
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              color="primary"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid var(--color-border-light)' }}>
        <Button onClick={onClose} style={{ color: 'var(--color-text-secondary)' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving || !formData.questionText.trim()}
          className={`${classes.actionButton} ${classes.primaryButton}`}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : editingQuestion ? 'Save Changes' : 'Add Question'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

QAFormTemplateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editingQuestion: PropTypes.shape({
    id: PropTypes.number,
    questionText: PropTypes.string,
    questionType: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })),
    isRequired: PropTypes.bool,
    displayOrder: PropTypes.number,
    isActive: PropTypes.bool
  }),
  classes: PropTypes.object.isRequired,
  formData: PropTypes.shape({
    questionText: PropTypes.string.isRequired,
    questionType: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })).isRequired,
    isRequired: PropTypes.bool.isRequired,
    displayOrder: PropTypes.number.isRequired,
    isActive: PropTypes.bool.isRequired
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  newOption: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }).isRequired,
  setNewOption: PropTypes.func.isRequired,
  handleAddOption: PropTypes.func.isRequired,
  handleRemoveOption: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired
}

export default QAFormTemplateDialog
